import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { examApi } from '../../services/api';
import { toast } from 'sonner';
import { ShieldAlert, Loader2 } from 'lucide-react';

const ACTION_COLOR = {
  created: 'secondary',
  edited: 'outline',
  submitted: 'default',
  approved: 'default',
  rejected: 'destructive',
  locked: 'default',
  viewed: 'outline',
  downloaded: 'outline',
};

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await examApi.listAudit({ limit: 500 });
        setLogs(data.logs || []);
      } catch (e) {
        toast.error('Failed to load audit logs');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
          <ShieldAlert className="h-6 w-6 text-primary" /> Audit Logs
        </h1>
        <p className="text-sm text-muted-foreground">
          Immutable activity trail for every paper · viewed, edited, downloaded, approved
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {loading ? 'Loading…' : `${logs.length} log entries`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : logs.length === 0 ? (
            <p className="text-center py-10 text-muted-foreground">No audit activity yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Paper ID</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => (
                    <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={ACTION_COLOR[log.action] || 'outline'}>
                          {log.action?.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{log.actor_email}</p>
                        <p className="text-xs text-muted-foreground">
                          {log.actor_role}{log.actor_designation ? ` · ${log.actor_designation}` : ''}
                        </p>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{log.paper_id?.slice(0, 8)}…</TableCell>
                      <TableCell className="font-mono text-xs">{log.ip_address || '—'}</TableCell>
                      <TableCell className="text-xs max-w-xs">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <code className="text-[10px]">{JSON.stringify(log.details)}</code>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
